import { useEffect, useRef, useState } from "react";
import { m, AnimatePresence } from "framer-motion";
import { ConfirmModal } from "../common/ConfirmModal";
import {
  BellRing,
  Building2,
  Check,
  CircleCheck,
  CircleX,
  Copy,
  KeyRound,
  LogOut,
  Mail,
  MapPin,
  Pencil,
  Phone,
  ShieldCheck,
  Users,
  X,
} from "lucide-react";
import { PillIcon } from "../common/PillIcon";
import { TONES } from "../filters/statusColors";
import clsx from "clsx";
import { isEmeisCalendarEnabled } from "../../utils/emeisModuleFlags";
import { Avatar } from "../common/Avatar";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

export interface UserDropdownData {
  name: string;
  role?: string;
  email?: string;
  phone?: string;
  location?: string;
  /** The signed-in member's own department and business unit. */
  departmentName?: string;
  businessUnitName?: string;
  /** Whether this member's record is active. Undefined renders no chip
   *  rather than guessing — an unknown state is not the same as inactive. */
  isActive?: boolean;
  joined?: string;
  avatarInitials?: string;
  avatarUrl?: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onLogout?: () => void;
  onEditProfile?: () => void;
  data: UserDropdownData;
}

export function UserDropdown({
  open,
  onClose,
  onLogout,
  onEditProfile,
  data,
}: Props) {
  const { t } = useTranslation("auth");
  const ref = useRef<HTMLDivElement>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  /** Which value was just copied, so only its own button shows the tick. */
  const [copied, setCopied] = useState<string | null>(null);
  const navigate = useNavigate();

  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(text);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      // Clipboard refused (permissions, insecure context): nothing to show.
    }
  };

  const statusTone = data.isActive ? TONES.success : TONES.danger;

  /** One labelled detail: an icon, a small caption and the value. */
  const detail = (
    key: string,
    Icon: typeof Mail,
    caption: string,
    value: string,
    link?: { href: string; title: string },
  ) => (
    <li key={key} className="group flex items-center gap-3 py-2.5">
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-[rgb(var(--card-muted))] text-[rgb(var(--text-secondary))]">
        <Icon size={15} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[11px] font-medium text-[rgb(var(--text-muted))]">
          {caption}
        </span>
        {link ? (
          <a
            href={link.href}
            title={link.title}
            className="block truncate text-sm font-medium text-[rgb(var(--text-primary))] underline-offset-2 hover:text-[rgb(var(--text-accent))] hover:underline"
          >
            {value}
          </a>
        ) : (
          <span className="block truncate text-sm font-medium text-[rgb(var(--text-primary))]">
            {value}
          </span>
        )}
      </span>
      {link && (
        <button
          type="button"
          onClick={() => handleCopy(value)}
          title={t("userDropdown.tooltips.clickToCopy")}
          aria-label={`${t("userDropdown.tooltips.clickToCopy")}: ${value}`}
          className="grid h-8 w-8 shrink-0 place-items-center rounded-md text-[rgb(var(--text-muted))] opacity-60 transition hover:bg-[rgb(var(--card-muted))] hover:text-[rgb(var(--text-primary))] hover:opacity-100 focus:outline-none focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-[rgb(var(--text-accent))] group-hover:opacity-100"
        >
          {copied === value ? (
            <Check size={14} className={TONES.success.text} />
          ) : (
            <Copy size={14} />
          )}
        </button>
      )}
    </li>
  );

  // Scroll lock
  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  // Outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onClose();
      }
    }

    if (open) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open, onClose]);

  // ESC close
  useEffect(() => {
    function handleEsc(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }

    if (open) document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [open, onClose]);

  return (
    <>
      <AnimatePresence>
        {open && (
          <>
            <m.div
              className="fixed inset-0 z-[99998] bg-black/30 backdrop-blur-sm"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={onClose}
            />

            <m.div
              ref={ref}
              initial={{ x: 80, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: 80, opacity: 0 }}
              transition={{ type: "spring", stiffness: 260, damping: 28 }}
              className="
                fixed top-0 right-0
                max-h-full w-[min(360px,100vw)] overflow-hidden
                z-[99999]
                flex flex-col
                bg-[rgb(var(--card))]
                border-l border-b border-[rgb(var(--border))]
                rounded-bl-2xl
                shadow-2xl
              "
            >
              <div className="flex-1 overflow-y-auto">
                {/* Identity: a soft accent band with the avatar resting on
                    its edge, then name, role and status. */}
                <div className="relative">
                  <div
                    aria-hidden
                    className="h-20 bg-gradient-to-br from-[rgb(var(--text-accent))]/25 via-[rgb(var(--text-accent))]/10 to-transparent"
                  />
                  <button
                    type="button"
                    onClick={onClose}
                    aria-label={t("topbar:userDropdown.close", {
                      defaultValue: "Close profile panel",
                    })}
                    title={t("topbar:userDropdown.close", {
                      defaultValue: "Close profile panel",
                    })}
                    className="absolute right-3 top-3 grid h-8 w-8 place-items-center rounded-lg bg-[rgb(var(--card))]/70 text-[rgb(var(--text-secondary))] backdrop-blur transition hover:bg-[rgb(var(--card))] hover:text-[rgb(var(--text-primary))] focus:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--text-accent))]"
                  >
                    <X size={16} />
                  </button>
                  <div className="-mt-10 flex flex-col items-center px-5 text-center">
                    <m.div
                      layoutId="avatarMorph"
                      className="rounded-full ring-4 ring-[rgb(var(--card))]"
                    >
                      <Avatar
                        src={data.avatarUrl}
                        label={data.name}
                        size={76}
                      />
                    </m.div>
                    <h3 className="mt-3 text-base font-semibold text-[rgb(var(--text-primary))]">
                      {data.name}
                    </h3>
                    <div className="mt-2 flex flex-wrap items-center justify-center gap-1.5">
                      {data.role && (
                        <span
                          className={clsx(
                            "inline-flex items-center gap-1.5 rounded-full py-0.5 pl-1 pr-2.5 text-xs font-semibold",
                            TONES.neutral.bg,
                            TONES.neutral.text,
                          )}
                        >
                          <PillIcon icon={ShieldCheck} />
                          {data.role}
                        </span>
                      )}
                      {/* Unknown state renders no chip rather than guessing. */}
                      {data.isActive !== undefined && (
                        <span
                          className={clsx(
                            "inline-flex items-center gap-1.5 rounded-full py-0.5 pl-1 pr-2.5 text-xs font-semibold",
                            statusTone.bg,
                            statusTone.text,
                          )}
                        >
                          <PillIcon
                            icon={data.isActive ? CircleCheck : CircleX}
                          />
                          {data.isActive
                            ? t("topbar:userDropdown.status.active", {
                                defaultValue: "Active",
                              })
                            : t("topbar:userDropdown.status.inactive", {
                                defaultValue: "Inactive",
                              })}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Details: each with a caption, so "Finance" reads as the
                    department rather than a bare word. */}
                <ul className="mx-5 mt-5 divide-y divide-[rgb(var(--border))]/70 rounded-2xl border border-[rgb(var(--border))] px-3">
                  {data.email &&
                    detail(
                      "email",
                      Mail,
                      t("userDropdown.labels.email", { defaultValue: "Email" }),
                      data.email,
                      {
                        href: `mailto:${data.email}`,
                        title: t("userDropdown.tooltips.clickToEmail"),
                      },
                    )}
                  {data.phone &&
                    detail(
                      "phone",
                      Phone,
                      t("userDropdown.labels.phone", { defaultValue: "Phone" }),
                      data.phone,
                      {
                        href: `tel:${data.phone.replace(/\s+/g, "")}`,
                        title: t("userDropdown.tooltips.clickToCall"),
                      },
                    )}
                  {data.businessUnitName &&
                    detail(
                      "bu",
                      Building2,
                      t("userDropdown.labels.businessUnit", {
                        defaultValue: "Business unit",
                      }),
                      data.businessUnitName,
                    )}
                  {data.departmentName &&
                    detail(
                      "dept",
                      Users,
                      t("userDropdown.labels.department", {
                        defaultValue: "Department",
                      }),
                      data.departmentName,
                    )}
                  {data.location &&
                    detail(
                      "loc",
                      MapPin,
                      t("userDropdown.labels.location", {
                        defaultValue: "Location",
                      }),
                      data.location,
                    )}
                </ul>

                <div className="m-5 space-y-2">
                  {/* Edit and Change Password share a row: the same kind of
                      action on the same record. */}
                  <div className="flex flex-wrap gap-2">
                    {onEditProfile && (
                      <button
                        type="button"
                        onClick={onEditProfile}
                        className="inline-flex h-10 flex-1 basis-[8rem] items-center justify-center gap-1.5 whitespace-nowrap rounded-lg bg-[rgb(var(--accent-fill))] px-3 text-sm font-medium text-[rgb(var(--accent-ink))] shadow-sm transition hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--text-accent))] focus-visible:ring-offset-2 focus-visible:ring-offset-[rgb(var(--card))]"
                      >
                        <Pencil size={14} />
                        {t("userDropdown.buttons.editProfile")}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        onClose();
                        navigate("/change-password");
                      }}
                      className={clsx(
                        "inline-flex h-10 flex-1 basis-[8rem] items-center justify-center gap-1.5 whitespace-nowrap rounded-lg border border-[rgb(var(--border))] bg-[rgb(var(--card))] px-3 text-sm font-medium text-[rgb(var(--text-primary))] transition hover:border-[rgb(var(--text-accent))]/50 hover:bg-[rgb(var(--card-muted))] focus:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--text-accent))]",
                      )}
                    >
                      <KeyRound size={14} />
                      {t("userDropdown.buttons.changePassword")}
                    </button>
                  </div>

                  {isEmeisCalendarEnabled() && (
                    <button
                      type="button"
                      onClick={() => {
                        onClose();
                        navigate("/notifications/preferences");
                      }}
                      className="inline-flex h-10 w-full items-center justify-center gap-1.5 rounded-lg border border-[rgb(var(--border))] bg-[rgb(var(--card))] px-3 text-sm font-medium text-[rgb(var(--text-primary))] transition hover:border-[rgb(var(--text-accent))]/50 hover:bg-[rgb(var(--card-muted))] focus:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--text-accent))]"
                    >
                      <BellRing size={14} />
                      {t("userDropdown.buttons.notificationPreferences", {
                        defaultValue: "Notification preferences",
                      })}
                    </button>
                  )}

                  {/* Sign-out is a way out, not a peer of the edits: a quiet
                      row set apart by a rule. */}
                  {onLogout && (
                    <div className="border-t border-[rgb(var(--border))] pt-2">
                      <button
                        type="button"
                        onClick={() => setConfirmOpen(true)}
                        className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-lg text-sm font-semibold text-rose-700 transition hover:bg-rose-500/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-500/50 dark:text-rose-300"
                      >
                        <LogOut size={15} />
                        {t("userDropdown.buttons.logout")}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </m.div>
          </>
        )}
      </AnimatePresence>

      {/* Confirm Logout */}
      <ConfirmModal
        open={confirmOpen}
        title={t("userDropdown.logoutModal.title")}
        description={t("userDropdown.logoutModal.description")}
        confirmLabel={t("userDropdown.logoutModal.confirmLabel")}
        variant="danger"
        onCancel={() => setConfirmOpen(false)}
        onConfirm={async () => {
          setConfirmOpen(false);
          onClose();
          onLogout?.();
        }}
      />
    </>
  );
}
